import { useState } from 'react';
import clsx from 'clsx';
import type { CollectionWithPois, CollectionVisibility } from '@poi-planner/shared';

interface CollectionCardProps {
  collection: CollectionWithPois;
  onDelete: (id: string) => void;
  onEdit: (collection: CollectionWithPois) => void;
  onShare?: (collectionId: string) => void;
  onClick?: (collection: CollectionWithPois) => void;
}

function VisibilityIcon({ visibility }: { visibility: CollectionVisibility }) {
  switch (visibility) {
    case 'private':
      return <span className="material-symbols-outlined text-sm">lock</span>;
    case 'public':
      return <span className="material-symbols-outlined text-sm">public</span>;
    case 'shared':
      return <span className="material-symbols-outlined text-sm">group</span>;
  }
}

function VisibilityBadge({ visibility }: { visibility: CollectionVisibility }) {
  const config = {
    private: { bg: '#f1f5f9', color: '#64748b', label: 'Private' },
    public: { bg: '#ecfdf5', color: '#10b981', label: 'Public' },
    shared: { bg: '#fff7ed', color: '#f97316', label: 'Shared' },
  }[visibility];

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold"
      style={{ background: config.bg, color: config.color }}
    >
      <VisibilityIcon visibility={visibility} />
      {config.label}
    </span>
  );
}

export function CollectionCard({ collection, onDelete, onEdit, onShare, onClick }: CollectionCardProps) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleClick = () => {
    onClick?.(collection);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showConfirm) {
      onDelete(collection.id);
    } else {
      setShowConfirm(true);
      setTimeout(() => setShowConfirm(false), 3000);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(collection);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(collection.id);
  };

  return (
    <div
      onClick={handleClick}
      className="group relative bg-white border border-border rounded-2xl p-5 cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-slate-200/50 hover:border-slate-300 hover:-translate-y-0.5"
    >
      {/* Permission Badge */}
      {!collection.isOwner && (
        <div
          className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold"
          style={{
            background: collection.permission === 'editor' ? '#fff7ed' : '#eff6ff',
            color: collection.permission === 'editor' ? '#f97316' : '#3b82f6',
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>
            {collection.permission === 'editor' ? 'edit' : 'visibility'}
          </span>
          <span>{collection.permission === 'editor' ? 'Editor' : 'Viewer'}</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="absolute top-3 right-3 flex items-center gap-1">
        {collection.isOwner && onShare && collection.visibility !== 'private' && (
          <button
            onClick={handleShare}
            className="flex items-center justify-center size-8 rounded-lg border bg-white border-border text-text-muted opacity-0 group-hover:opacity-100 transition-all duration-200 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50"
            title="Share collection"
          >
            <span className="material-symbols-outlined text-lg">share</span>
          </button>
        )}
        {collection.isOwner && (
          <button
            onClick={handleEdit}
            className="flex items-center justify-center size-8 rounded-lg border bg-white border-border text-text-muted opacity-0 group-hover:opacity-100 transition-all duration-200 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50"
            title="Edit collection"
          >
            <span className="material-symbols-outlined text-lg">edit</span>
          </button>
        )}
        {collection.isOwner && (
          <button
            onClick={handleDelete}
            className={clsx(
              'flex items-center justify-center size-8 rounded-lg border transition-all duration-200',
              showConfirm
                ? 'bg-red-500 border-red-500 text-white opacity-100'
                : 'bg-white border-border text-text-muted opacity-0 group-hover:opacity-100 hover:border-red-200 hover:text-red-500 hover:bg-red-50'
            )}
            title={showConfirm ? 'Click again to confirm' : 'Delete collection'}
          >
            <span className="material-symbols-outlined text-lg">
              {showConfirm ? 'check' : 'delete'}
            </span>
          </button>
        )}
      </div>

      {/* Collection icon */}
      <div className="flex items-center justify-center size-12 rounded-xl bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-200/50 mb-4">
        <span className="material-symbols-outlined text-2xl text-amber-600">folder_special</span>
      </div>

      {/* Collection name */}
      <h3 className="text-base font-bold text-text-primary mb-1 pr-8 line-clamp-2">
        {collection.name}
      </h3>

      {/* Description */}
      {collection.description && (
        <p className="text-sm text-text-secondary mb-3 line-clamp-2">
          {collection.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-base text-text-muted">location_on</span>
          <span className="text-xs font-semibold text-text-muted">
            {collection.poiIds.length} {collection.poiIds.length === 1 ? 'POI' : 'POIs'}
          </span>
        </div>
        <VisibilityBadge visibility={collection.visibility} />
      </div>
    </div>
  );
}
